import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useAuth from '../hooks/useAuth';
import {
  listConnections, browse, folderSize, listFolders, createFolder, updateFolder, deleteFolder,
} from '../api/networkDriveAPI';
import { getAllEnvironments, getEnvironmentById, getTeams } from '../api/documentAPI';
import { useNotification } from '../context/NotificationContext';
import { NotificationType } from '../enums/NotificationType';
import FolderTree from '../components/sharepoint/FolderTree';
import SingleSelect from '../components/shared/SingleSelect';
import TeamsMultiselect, { FolderAudience, toTeamOptions } from '../components/shared/TeamsMultiselect';
import ConfirmDeleteModal from '../components/shared/ConfirmDeleteModal';
import FileTypeFilter from '../components/sync/FileTypeFilter';
import { MB_PRESETS, decodeExtensions, encodeExtensions } from '../components/sync/fileTypePresets';
import '../css/pages/sharepoint.css';


const NetworkDriveConnection = () => {
  const { id } = useParams();
  const connId = Number(id);
  const { auth } = useAuth();
  const navigate = useNavigate();
  const [t] = useTranslation('global');
  const { showNotification } = useNotification();

  const [conn, setConn] = useState(null);
  const [rootData, setRootData] = useState(null);
  const [browseError, setBrowseError] = useState(null);
  const [browsing, setBrowsing] = useState(true);
  const [envs, setEnvs] = useState([]);
  const [mappings, setMappings] = useState([]);
  // Nastavenia dát sú per priečinok — plnia sa pri editácii mapovania (startEdit), nie z pripojenia.
  // excludedExt = vylúčené podporované prípony (prázdne = synchronizujú sa všetky dokumenty)
  const [dataSettings, setDataSettings] = useState({ excludedExt: [], FileSizeLimitMB: 50 });
  const [customLimit, setCustomLimit] = useState(false); // „Vlastné…" MB namiesto predvoleného zoznamu

  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [root, setRoot] = useState(null);
  const [excluded, setExcluded] = useState(new Set());
  const [target, setTarget] = useState('');
  // Pri pridaní je predvolené vytvoriť nový priečinok v Millade pomenovaný podľa vybraného priečinka
  const [targetMode, setTargetMode] = useState('new');
  const [newEnvName, setNewEnvName] = useState('');
  const [nameTouched, setNameTouched] = useState(false); // ručne upravený názov sa už nepredvypĺňa
  // Prístup k novému priečinku: null = predvolene tímy, v ktorých je správca (vidí ho hneď)
  const [teamOptions, setTeamOptions] = useState([]);
  const [newEnvTeams, setNewEnvTeams] = useState(null);
  const [targetTeamIds, setTargetTeamIds] = useState(null); // tímy existujúceho cieľového priečinka
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const loadTree = () => {
    setBrowsing(true); setBrowseError(null); setRootData(null);
    browse(connId, null)
      .then(d => setRootData({ folders: d.folders || [], files: d.files || [] }))
      .catch(e => setBrowseError(e?.response?.data?.detail || String(e)))
      .finally(() => setBrowsing(false));
  };
  const fetchMappings = () => listFolders(connId).then(d => setMappings(d || [])).catch(() => setMappings([]));

  useEffect(() => {
    if (Number(auth?.uri) !== 1) { navigate('/Unauthorized'); return; }
    listConnections().then(list => {
      const connection = (list || []).find(c => c.Id === connId) || null;
      setConn(connection);
    }).catch(() => {});
    getAllEnvironments().then(d => setEnvs(d || [])).catch(() => {});
    getTeams().then(d => setTeamOptions(toTeamOptions(d))).catch(() => {});
    fetchMappings();
    loadTree();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // kto uvidí existujúci cieľový priečinok (jeho tímy) — pri výbere v „Existující“ aj pri úprave mapovania
  useEffect(() => {
    setTargetTeamIds(null);
    if (!target) return undefined;
    let current = true;
    getEnvironmentById(Number(target))
      .then(d => { if (current) setTargetTeamIds(d?.DocsEnv?.TeamIds || []); })
      .catch(() => {});
    return () => { current = false; };
  }, [target]);

  const envName = (eid) => (envs.find(e => e.Id === eid)?.Name) || `#${eid}`;

  const resetFlow = () => {
    setAdding(false); setEditingId(null); setRoot(null); setExcluded(new Set()); setTarget('');
    setTargetMode('new'); setNewEnvName(''); setNameTouched(false); setNewEnvTeams(null);
    // Nové mapovanie: synchronizujú sa všetky podporované dokumenty
    setDataSettings({ excludedExt: [], FileSizeLimitMB: 50 });
    setCustomLimit(false);
  };
  const startAdd = () => { resetFlow(); setAdding(true); };
  const startEdit = (m) => {
    const limit = m.FileSizeLimitMB ?? 50;
    setEditingId(m.Id);
    setRoot(m.FolderPath);
    setExcluded(new Set(m.ExcludePaths || []));
    setTarget(String(m.DocsEnvId));
    setTargetMode('existing');
    setDataSettings({ excludedExt: decodeExtensions(m.ExcludedFileExtensions), FileSizeLimitMB: limit });
    setCustomLimit(!MB_PRESETS.includes(Number(limit)));
    setAdding(true);
  };

  const handleLimitChange = (v) => {
    if (v === 'custom') {
      setCustomLimit(true);
    } else {
      setCustomLimit(false);
      setDataSettings(current => ({ ...current, FileSizeLimitMB: Number(v) }));
    }
  };

  // single-root výber v strome
  const toggleNode = (rel, isFolder) => {
    if (root === null) { if (isFolder) setRoot(rel); return; }
    if (rel === root) { setRoot(null); setExcluded(new Set()); return; }
    if (root === '' || rel.startsWith(root + '/')) {
      setExcluded(prev => {
        const n = new Set(prev);
        if (n.has(rel)) { n.delete(rel); return n; }
        let viaAnc = false;
        for (const e of n) if (rel.startsWith(e + '/')) { n.delete(e); viaAnc = true; }  // e nikdy nie je ''
        if (!viaAnc) n.add(rel);
        return n;
      });
      return;
    }
    if (isFolder) { setRoot(rel); setExcluded(new Set()); } // prepni root
  };

  const creatingEnv = !editingId && targetMode === 'new';
  const chosenTeams = newEnvTeams ?? teamOptions.filter(team => team.isMember).map(team => team.value);

  const handleSave = async () => {
    if (root === null) { showNotification({ message: t('selectFolders') || 'Vyber priečinok', type: NotificationType.Error }); return; }
    const envNameValue = newEnvName.trim();
    if (creatingEnv && (envNameValue.length < 3 || envNameValue.length > 63)) {
      showNotification({ message: t('envNameRuleMsg') || 'Názov priečinka musí mať od 3 do 63 znakov.', type: NotificationType.Error });
      return;
    }
    if (!creatingEnv && !target) { showNotification({ message: t('pickTarget') || 'Vyber cieľový priečinok', type: NotificationType.Error }); return; }
    const limit = Number(dataSettings.FileSizeLimitMB);
    if (!Number.isFinite(limit) || limit <= 0) {
      showNotification({ message: t('invalidFileSizeLimit') || 'Limit veľkosti musí byť väčší ako 0 MB.', type: NotificationType.Error });
      return;
    }
    setSaving(true);
    const payload = {
      FolderPath: root,
      ...(creatingEnv ? { NewDocsEnvName: envNameValue, NewDocsEnvTeamIds: chosenTeams } : { DocsEnvId: Number(target) }),
      ExcludePaths: [...excluded],
      ExcludedFileExtensions: encodeExtensions(dataSettings.excludedExt),
      FileSizeLimitMB: limit,
    };
    try {
      if (editingId) await updateFolder(editingId, payload);
      else await createFolder(connId, payload);
      showNotification({ message: t('mappingSaved') || t('savedChanges'), type: NotificationType.Success });
      if (creatingEnv) getAllEnvironments().then(d => setEnvs(d || [])).catch(() => {});
      resetFlow();
      fetchMappings();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      showNotification({ message: typeof detail === 'string' ? detail : t('errorOccured'), type: NotificationType.Error });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await deleteFolder(toDelete.Id);
      showNotification({ message: t('savedChanges'), type: NotificationType.Success });
      fetchMappings();
    } catch (e) {
      showNotification({ message: t('errorOccured'), type: NotificationType.Error });
    } finally { setToDelete(null); }
  };

  const folderName = (url) => {
    const parts = String(url || '').split('/').filter(Boolean);
    return parts[parts.length - 1] || url;
  };

  // „Firma - priečinok“: firma = názov pripojenia po prvú zátvorku, priečinok = posledná časť výberu
  // (celý disk -> posledná časť cesty disku)
  const suggestEnvName = (rel) => {
    const folder = rel ? folderName(rel) : folderName(String(conn?.RootPath || '').replace(/\\/g, '/'));
    const company = String(conn?.Name || '').split('(')[0].trim();
    return company && company.toLowerCase() !== String(folder).toLowerCase() ? `${company} - ${folder}` : folder;
  };

  useEffect(() => {
    if (editingId === null && !nameTouched) setNewEnvName(root === null ? '' : suggestEnvName(root));
  }, [root, conn]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderTargetMode = () => (
    <div className="sp-target-mode" role="radiogroup" aria-label={t('targetEnvironment') || 'Cieľový priečinok'}>
      {[['new', 'create_new_folder', t('targetModeNew') || 'Vytvoriť nový'],
        ['existing', 'folder_open', t('targetModeExisting') || 'Existujúci']].map(([mode, icon, label]) => (
        <button
          key={mode}
          type="button"
          role="radio"
          aria-checked={targetMode === mode}
          className={`sp-target-mode-btn${targetMode === mode ? ' selected' : ''}`}
          onClick={() => setTargetMode(mode)}
        >
          <span className="material-symbols-outlined">{icon}</span>{label}
        </button>
      ))}
    </div>
  );

  const renderFolderEditor = () => (
    <div className="sp-folder-editor">
      {!editingId && (
        <div className="sp-editing-banner">
          <span className="material-symbols-outlined">edit</span>
          <div>
            <strong>{t('selectFolderToSync') || 'Vyberte priečinok na synchronizáciu'}</strong>
            {root !== null && <span>{root === '' ? (t('wholeDrive') || 'Celý disk') : root}</span>}
          </div>
        </div>
      )}
      <div className="sp-library-heading">
        <span className="material-symbols-outlined">folder_special</span>
        <span>{t('driveFolders') || 'Priečinky na disku'}</span>
      </div>
      <p className="sp-steps">{t('treeHint') || 'Zaškrtni priečinok (synchronizuje sa celý jeho obsah). Jednotlivé podpriečinky/súbory vieš vyňať.'}</p>
      <div className="sp-tree-wrap">
        <FolderTree connId={connId} rootData={rootData} root={root} excluded={excluded} onToggle={toggleNode} browseFn={browse} sizeFn={folderSize} rootLabel={`${t('wholeDrive') || 'Celý disk'} — ${conn?.RootPath || ''}`} />
      </div>

      {/* Nastavenia dát — per priečinok (najprv ČO sa synchronizuje, potom KAM a kto to uvidí) */}
      <div className="sp-data-settings-panel sp-data-in-editor sp-data-side">
        <FileTypeFilter
          key={editingId ?? 'new'}
          excluded={dataSettings.excludedExt}
          onChange={excludedExt => setDataSettings(current => ({ ...current, excludedExt }))}
        />

        <div className="input-line sp-size-limit">
          <label>{t('fileSizeLimit') || 'Limit veľkosti'}</label>
          <div className="sp-size-limit-controls">
            <SingleSelect
              className="single-select--soft sp-size-select"
              value={customLimit ? 'custom' : String(dataSettings.FileSizeLimitMB)}
              onChange={handleLimitChange}
              options={[
                ...MB_PRESETS.map(n => ({ value: n, label: `${n} MB` })),
                { value: 'custom', label: t('customValue') || 'Vlastné…' },
              ]}
            />
            {customLimit && (
              <div className="sp-number-unit">
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={dataSettings.FileSizeLimitMB}
                  onChange={e => setDataSettings(current => ({ ...current, FileSizeLimitMB: e.target.value }))}
                />
                <span>MB</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="input-line sp-target-row">
        <label>{t('targetEnvironment') || 'Cieľový priečinok'}</label>
        <div className="sp-target-body">
          {!editingId && renderTargetMode()}
          {creatingEnv ? (
            <div className="sp-new-env">
              <label className="sp-field-label" htmlFor="sp-new-env-name">{t('newEnvName') || 'Názov priečinka'}</label>
              <input
                id="sp-new-env-name"
                type="text"
                className="sp-new-env-name"
                value={newEnvName}
                maxLength={63}
                onChange={e => { setNewEnvName(e.target.value); setNameTouched(true); }}
                placeholder={t('newEnvNamePlaceholder') || 'Najprv vyberte priečinok v strome'}
              />
              <div className="sp-target-teams">
                <TeamsMultiselect teams={teamOptions} selectedValues={chosenTeams} onChange={e => setNewEnvTeams(e.target.value)} />
              </div>
              <span className="sp-hint">{t('newEnvHint') || 'Vytvorí sa priečinok typu Zdieľaný disk so spracovaním offline s OCR (zmeníte v Dokumentoch).'}</span>
            </div>
          ) : envs.some(e => e.Type === 'N') ? (
            <>
              <SingleSelect
                className="single-select--soft sp-target-select"
                value={target}
                onChange={v => setTarget(String(v))}
                placeholder={t('pickTarget') || '— vyber cieľový priečinok —'}
                options={envs.filter(e => e.Type === 'N').map(e => ({ value: e.Id, label: e.Name }))}
              />
              {targetTeamIds && (
                <div className="sp-target-teams">
                  <FolderAudience teams={teamOptions} selectedValues={targetTeamIds} />
                </div>
              )}
            </>
          ) : (
            <span className="sp-hint">{t('noNetworkDriveFolders') || 'Najprv vytvorte priečinok typu Zdieľaný disk v Dokumentoch.'}</span>
          )}
        </div>
      </div>

      <div className="sp-flow-actions sp-editor-actions">
        <button className="return-button" disabled={saving} onClick={resetFlow}>{t('cancel')}</button>
        <button className="submit-button" disabled={saving} onClick={handleSave}>{t('save')}</button>
      </div>
    </div>
  );

  return (
    <div className="documents-wrap sp-page">
      <button className="return-button return-button-top" onClick={() => navigate('/NetworkDrive')}>
        <span className="material-symbols-outlined">arrow_back</span>{t('backToConnections') || 'Späť na pripojenia'}
      </button>
      <div className="header-title sp-detail-title">
        <div>
          <h1>{conn ? (conn.Name || conn.RootPath) : (t('connection') || 'Pripojenie')}</h1>
          {conn?.RootPath && <div className="sp-detail-site-url">{conn.RootPath}</div>}
        </div>
        {!browsing && (browseError
          ? <span className="sp-status err">{t('connectionError') || 'Chyba'}</span>
          : <span className="sp-status on">{t('connected') || 'Pripojené'}</span>)}
      </div>

      {browsing && <div className="sp-card"><div className="sp-loading">{t('loading') || 'Načítavam'}…</div></div>}

      {!browsing && browseError && (
        <div className="sp-card">
          <div className="sp-errbox">
            <span className="material-symbols-outlined">error</span>
            <div>
              <div className="sp-errbox-title">{t('browseFailed') || 'Nepodarilo sa načítať priečinky'}</div>
              <div className="sp-errbox-detail">{browseError}</div>
            </div>
          </div>
          <button className="submit-button" onClick={loadTree}>{t('retry') || 'Skúsiť znova'}</button>
        </div>
      )}

      {!browsing && !browseError && (
        <>
          {/* Zoznam existujúcich mapovaní */}
          <div className="sp-card sp-card-flat">
            <div className="sp-card-head">
              <h3>{t('syncedFolders') || 'Synchronizované priečinky'}</h3>
              {!adding && <button className="submit-button sp-btn-compact" onClick={startAdd}><span className="material-symbols-outlined">add</span>&nbsp;{t('addFolder') || 'Pridať priečinok'}</button>}
            </div>
            {mappings.length === 0 && !adding && <div className="sp-invite">{t('noFoldersYet') || 'Zatiaľ žiadne priečinky. Pridaj priečinok na synchronizáciu.'}</div>}
            <ul className="sp-mappings sp-mapping-cards">
              {mappings.map(m => (
                <li key={m.Id} className={`sp-mapping-card${editingId === m.Id ? ' editing' : ''}`}>
                  <div
                    className="sp-mapping-card-summary"
                    role="button"
                    tabIndex={0}
                    onClick={() => (editingId === m.Id ? resetFlow() : startEdit(m))}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); editingId === m.Id ? resetFlow() : startEdit(m); } }}
                  >
                    <span className="sp-mapping-icon"><span className="material-symbols-outlined">folder</span></span>
                    <div className="sp-mapping-details">
                      <strong>{m.FolderPath ? folderName(m.FolderPath) : (t('wholeDrive') || 'Celý disk')}</strong>
                      <span className="sp-mapping-path" title={m.FolderPath || conn?.RootPath}>{m.FolderPath || conn?.RootPath}</span>
                    </div>
                    <div className="sp-mapping-meta">
                      <span className="sp-target-badge">
                        <span className="material-symbols-outlined">folder</span>
                        {envName(m.DocsEnvId)}
                      </span>
                      {m.ExcludePaths?.length > 0 && (
                        <span className="sp-excluded-badge">{m.ExcludePaths.length} {t('excludedCount') || 'vyňaté'}</span>
                      )}
                    </div>
                    <span className="sp-mapping-actions">
                      <button type="button" className="sp-icon-btn danger" onClick={(e) => { e.stopPropagation(); setToDelete(m); }} title={t('delete')} aria-label={t('delete')}>
                        <span className="material-symbols-outlined">delete</span>
                      </button>
                      <span className={`material-symbols-outlined sp-mapping-caret${editingId === m.Id ? ' open' : ''}`} aria-hidden="true">expand_more</span>
                    </span>
                  </div>
                  {editingId === m.Id && renderFolderEditor()}
                </li>
              ))}
            </ul>
            {adding && editingId === null && <div className="sp-new-folder-editor">{renderFolderEditor()}</div>}
          </div>
        </>
      )}

      <ConfirmDeleteModal
        open={toDelete !== null}
        onClose={() => setToDelete(null)}
        onNo={() => setToDelete(null)}
        onYes={handleDelete}
        item={toDelete ? (toDelete.FolderPath || conn?.RootPath || '') : ''}
      />
    </div>
  );
};

export default NetworkDriveConnection;
